import { ApiError } from '../core/network/api';
import { amountMarkup } from './ui/currency';
import { CHARACTER } from './config/character-tuning';
import { talentCopy, effectLine, talentIconFallback } from './config/talent-copy';
import type { CharacterClassId } from './config/character-roster';
import { iconMarkup, wireIconFallbacks } from './ui/icon';
import { classDisplayName } from './ui/class-picker';
import { t, tp } from './i18n/i18n';
import { serverText } from './config/served-text';
import {
  listCharacters, allocateTalent, respecCharacter, getTalentTree,
  type CharacterRow, type TalentNodeRow,
} from './net/character-api';

/**
 * design/gdd/talent-trees.md — the in-hub talent-allocation + respec UI for
 * an existing character (the creation wizard's own talent step only ever
 * covers the single free point granted at creation; this is where every
 * later level's point, and a respec, actually happen).
 *
 * Rebuilt 2026-08-25 from a flat list of text buttons into an actual tree.
 * The old version rendered `nodeId.replace(/_/g, ' ')`, so a node read
 * "light feet 0/3" and nothing on screen said what taking it would do —
 * a build system the player could operate but not read. What it now draws:
 * a painted icon per node, tier rows joined by connectors that light up as
 * the tier below unlocks, rank pips, and a detail panel carrying the name,
 * the effect and the flavor.
 *
 * **Interaction: inspect first, learn second.** A node click selects and
 * describes; a separate, labelled "Learn" button spends the point. Two
 * reasons, both load-bearing:
 *  - CLAUDE.md's mobile rule — "no hover-dependent mechanics". A tree where
 *    you must hover to find out what a node does before clicking it is
 *    unusable on a phone, which is the primary input here.
 *  - A talent point is scarce (15 in a character's life) and unwinding one
 *    costs RESPEC_COST_WAX. A stray tap on a 56px target must not spend it.
 * **Hover no longer selects (2026-09-04).** User: "I should have to click and
 * select the skill I want to upgrade, not just hover over it with the mouse."
 * It used to inspect on `mouseenter` as a desktop shortcut, and the shortcut
 * was the problem: crossing the tree to reach the Learn button dragged the
 * detail panel through every node on the way, so the thing you were reading
 * was replaced by the thing you were merely travelling over — and with the
 * tree now four columns wide and seven rows deep, there is a lot more to
 * travel over. Selection is a decision, and decisions are made by pressing
 * something. Keyboard focus no longer selects either, for the same reason:
 * tabbing past a node is passing it, not choosing it.
 *
 * Locked tiers stay inspectable on purpose. Reading the capstone you cannot
 * afford yet is how a player plans which tier-1 nodes to buy.
 */

// talent-trees.md Core Rule #1 — mirrors backend's TIER_UNLOCK_THRESHOLD.
type Tier = 1 | 2 | 3 | 4 | 5 | 6 | 7;
const TIER_UNLOCK_THRESHOLD: Record<Tier, number> = { 1: 0, 2: 3, 3: 6, 4: 9, 5: 13, 6: 17, 7: 21 };
const TIER_NUMERAL: Record<Tier, string> = {
  1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V', 6: 'VI', 7: 'VII',
};
/** `Tier IV`, in the interface language. Render time only. */
const tierLabel = (tier: Tier): string => t('Tier {n}', { n: TIER_NUMERAL[tier] });
const ALL_TIERS: Tier[] = [1, 2, 3, 4, 5, 6, 7];
/**
 * The tier that holds the capstones, read from the data rather than assumed.
 *
 * The last tier moved from 5 to 7 on 2026-09-04, and every `=== 5` in this
 * file was a place that would have kept drawing a capstone row in the middle
 * of the tree. One constant, derived once.
 */
const CAPSTONE_TIER: Tier = 7;
const ICON_BASE = '/assets/ui/talents';

/** Local, matching the file-local convention already used by the hub and the creation wizard. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string
  ));
}

/** Server text is English; known sentences translate through `t()`, the rest read as sent. */
function errorMessage(err: unknown): string {
  const message = err instanceof ApiError ? err.message : (err as Error)?.message;
  return message ? serverText(message) : t('Something went wrong');
}

export function openTalentManager(characterId: string, onChanged: () => void | Promise<void>): void {
  const overlay = document.createElement('div');
  // Full-screen rather than a modal card: seven tiers of four sockets plus a
  // capstone trio does not fit a 720px box without shrinking the sockets back
  // to the size that made the old tree unreadable. The column scrolls.
  overlay.className = 'fixed inset-0 z-50 bg-lobby flex flex-col';
  overlay.innerHTML = `
    <header class="shrink-0 flex items-center gap-3 px-3 sm:px-6 py-3 border-b border-border/60 bg-bg/70">
      <div class="min-w-0 mr-auto">
        <h2 id="tm-title" class="font-heading text-gold text-lg sm:text-2xl leading-tight tracking-wide break-words hyphens-auto">${escapeHtml(t('Talents'))}</h2>
        <p id="tm-sub" class="eyebrow eyebrow-dim"></p>
      </div>
      <span id="tm-points" class="chip chip-gold min-h-11 px-3 text-[13px] tabular-nums shrink-0"></span>
      <button id="tm-close" type="button" aria-label="${escapeHtml(t('Close talents'))}"
        class="btn btn-quiet min-w-11 px-0 justify-center shrink-0">${iconMarkup('close')}</button>
    </header>

    <div class="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div class="min-h-0 overflow-y-auto px-3 sm:px-6 py-4">
        <div id="tm-error" class="mb-3 notice-error" hidden></div>
        <div id="tm-tree" class="flex flex-col gap-1 max-w-[760px] mx-auto"></div>
      </div>

      <aside class="min-h-0 lg:overflow-y-auto border-t lg:border-t-0 lg:border-l border-border/60 bg-bg/60 p-3 sm:p-4 flex flex-col gap-3">
        <div id="tm-detail" class="frame-pewter p-3.5 flex flex-col gap-2.5"></div>
        <button id="tm-respec" type="button"
          class="btn btn-stone gap-2">
          <img src="${ICON_BASE}/_respec.webp" alt="" aria-hidden="true" class="w-5 h-5 rounded-sm opacity-80" />
          <span class="inline-flex items-center gap-1">${escapeHtml(t('Respec'))}${amountMarkup('wax', CHARACTER.RESPEC_COST_WAX, { hideLabel: true })}</span>
        </button>
      </aside>
    </div>
  `;
  document.body.appendChild(overlay);

  const $ = <T extends HTMLElement>(id: string) => overlay.querySelector(`#${id}`) as T;
  const titleEl = $<HTMLHeadingElement>('tm-title');
  const subEl = $<HTMLParagraphElement>('tm-sub');
  const errEl = $<HTMLDivElement>('tm-error');
  const pointsEl = $<HTMLSpanElement>('tm-points');
  const treeEl = $<HTMLDivElement>('tm-tree');
  const detailEl = $<HTMLDivElement>('tm-detail');
  const respecBtn = $<HTMLButtonElement>('tm-respec');
  const closeBtn = $<HTMLButtonElement>('tm-close');

  let character: CharacterRow | null = null;
  let tree: TalentNodeRow[] = [];
  let selectedId: string | null = null;
  let busy = false;

  function close(): void {
    document.removeEventListener('keydown', onKeydown);
    overlay.remove();
  }
  const onKeydown = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKeydown);

  function showError(message: string): void { errEl.textContent = message; errEl.hidden = false; }
  function hideError(): void { errEl.hidden = true; }

  function totalSpent(talents: Record<string, number>): number {
    // One tree per class (talent-trees.md Formula 1) — total spent doubles as spent-in-tree.
    return Object.values(talents).reduce((sum, rank) => sum + rank, 0);
  }

  /** Everything the render needs to know about one node, in one place. */
  interface NodeView {
    node: TalentNodeRow;
    rank: number;
    locked: boolean;
    maxed: boolean;
    /** A capstone the player cannot take because the other one is already theirs. */
    blockedByCapstone: boolean;
    /** Whether a point could be spent here right now. */
    learnable: boolean;
  }

  /** Core Rule #2 — the tier-5 capstone already taken, if any. */
  function takenCapstoneId(): string | null {
    const talents = character?.talents ?? {};
    for (const n of tree) if (n.isCapstone && (talents[n.id] ?? 0) > 0) return n.id;
    return null;
  }

  function viewFor(node: TalentNodeRow): NodeView {
    const talents = character?.talents ?? {};
    const rank = talents[node.id] ?? 0;
    const spent = totalSpent(talents);
    const unspent = (character?.talentPointsAvailable ?? 0) - (character?.talentPointsSpent ?? 0);
    const locked = spent < (TIER_UNLOCK_THRESHOLD[node.tier as Tier] ?? 0);
    const maxed = rank >= node.maxRank;
    const taken = takenCapstoneId();
    const blockedByCapstone = !!node.isCapstone && !!taken && taken !== node.id;
    return {
      node, rank, locked, maxed, blockedByCapstone,
      learnable: !locked && !maxed && !blockedByCapstone && unspent > 0 && !busy,
    };
  }

  /**
   * A node's picture, with the field emblem behind it.
   *
   * The 7-tier expansion (2026-09-04) added 52 nodes without art, so the src
   * is a file that may not exist. `data-fallback` carries what to draw
   * instead — see `talentIconFallback` — and `wireIconFallbacks` swaps it in
   * on the load error. Doing it on `error` rather than from a manifest means
   * the day real art ships for a node, the fallback simply stops firing and
   * nothing here has to be told about it.
   */
  function iconImgHtml(node: TalentNodeRow, className: string): string {
    const fallback = talentIconFallback(node.id, node.field);
    return `<img src="${ICON_BASE}/${escapeHtml(node.id)}.webp"
      data-fallback="${ICON_BASE}/${escapeHtml(fallback)}.webp"
      alt="" aria-hidden="true" class="${className}" loading="lazy" />`;
  }

  function pipsHtml(view: NodeView): string {
    const pips = Array.from({ length: view.node.maxRank }, (_, i) =>
      `<i class="talent-pip ${i < view.rank ? 'talent-pip-on' : ''}"></i>`).join('');
    return `<span class="flex items-center gap-1 h-2">${pips}</span>`;
  }

  /**
   * One socket: picture, name, pips, rank.
   *
   * The name box is a **fixed two lines tall** rather than as tall as the name
   * needs. At four across, "Weapon Mastery" wraps and its neighbours do not,
   * and a variable-height label pushes that column's pips and rank counter
   * down out of line with the rest of the row — which reads as a broken grid
   * rather than as a long word. 2.6em, not 2.3: `leading-tight` is 1.25, so
   * two lines need 2.5em and a shorter box clips the descenders off the second
   * one — "Weapon Mastery" rendered as "Weapon Masterv".
   */
  function nodeHtml(view: NodeView, big = false): string {
    const { node, rank, locked, maxed, learnable } = view;
    const copy = talentCopy(node.id);
    // Four across a 390px phone: 4 x 60px plus three 6px gaps is 258px inside
    // a 342px content box, which leaves the label room to be two lines. The
    // socket is smaller than the 64px it was at three across, and the button
    // around it keeps `min-h-11` so the tap target does not shrink with it.
    const size = big ? 'w-[76px] h-[76px] sm:w-[96px] sm:h-[96px]' : 'w-[60px] h-[60px] sm:w-[72px] sm:h-[72px]';
    const state = locked || view.blockedByCapstone
      ? 'talent-socket-locked'
      : maxed ? 'talent-socket-maxed' : learnable ? 'talent-socket-open' : '';
    const active = node.id === selectedId ? 'talent-socket-active' : '';
    return `
      <button type="button" data-node="${escapeHtml(node.id)}" aria-pressed="${node.id === selectedId}"
        title="${escapeHtml(copy.name)}"
        class="flex flex-col items-center gap-1.5 min-h-11">
        <span class="talent-socket ${state} ${active} ${size} rounded grid place-items-center overflow-hidden">
          ${iconImgHtml(node, 'w-full h-full object-cover')}
          ${locked || view.blockedByCapstone ? `<img src="${ICON_BASE}/_lock.webp" alt="" aria-hidden="true"
            class="absolute inset-0 m-auto w-8 h-8 rounded-sm opacity-95" style="filter:grayscale(0) brightness(2.4)" />` : ''}
        </span>
        <span class="block text-[11px] sm:text-[12px] leading-tight text-center min-h-[2.6em]
          ${maxed ? 'text-gold' : 'text-text-dim'} max-w-[86px] break-words hyphens-auto">${escapeHtml(copy.name)}</span>
        ${pipsHtml(view)}
        <span class="text-[10px] text-text-dim tabular-nums">${rank}/${node.maxRank}</span>
      </button>
    `;
  }

  /**
   * The join between two tier rows: a stem down from each column, a rail
   * across, and — into the capstone tier, which is a centred either/or rather
   * than a fourth column — one stem back down to the middle.
   */
  function connectorHtml(nextUnlocked: boolean, funnelToCentre: boolean): string {
    const stem = nextUnlocked ? 'talent-stem-lit' : 'talent-stem';
    const rail = nextUnlocked ? 'talent-rail-lit' : 'talent-rail';
    // Built as named pieces rather than inline `'a' + stem + 'b'.repeat(3)`:
    // `.repeat` binds to the last string literal alone, not to the whole
    // concatenation, which emitted one column plus two orphaned `"></i></span>`
    // fragments as visible text in the middle of the tree.
    const column = `<span class="h-2 flex justify-center"><i class="w-0.5 h-full ${stem}"></i></span>`;
    // Four since 2026-09-04, matching the row above it. A three-column rail
    // under a four-column tier draws stems into the gaps between sockets,
    // which reads as a rendering fault rather than as a tree.
    const columnRow = `<div class="grid grid-cols-4 w-full">${column.repeat(4)}</div>`;
    return `
      <div class="flex flex-col items-center" aria-hidden="true">
        ${columnRow}
        <div class="h-0.5 w-[86%] ${rail}"></div>
        ${funnelToCentre ? `<i class="w-0.5 h-2 ${stem}"></i>` : columnRow}
      </div>
    `;
  }

  function tierHeaderHtml(tier: Tier, locked: boolean, spent: number): string {
    const need = TIER_UNLOCK_THRESHOLD[tier];
    return `
      <div class="flex items-baseline gap-2 mb-1.5">
        <span class="eyebrow ${locked ? 'eyebrow-dim' : ''}">${escapeHtml(tierLabel(tier))}</span>
        <span class="h-px flex-1 ${locked ? 'bg-border' : 'bg-gold/30'}"></span>
        <span class="text-[11px] text-text-dim tabular-nums">${escapeHtml(locked ? t('{spent}/{need} points', { spent, need }) : t('open'))}</span>
      </div>
    `;
  }

  function renderTree(): void {
    if (!character) return;
    const talents = character.talents ?? {};
    const spent = totalSpent(talents);

    treeEl.innerHTML = ALL_TIERS.map((tier) => {
      const nodes = tree.filter((n) => n.tier === tier);
      if (nodes.length === 0) return '';
      const locked = spent < TIER_UNLOCK_THRESHOLD[tier];
      // Tier 5 is the capstone pair (Core Rule #2) — two of them, and taking
      // one forecloses the other, so it is laid out as a visible either/or
      // rather than as a third row of three.
      const body = tier === CAPSTONE_TIER
        ? `
          <div class="flex flex-col items-center gap-2">
            <p class="font-heading font-bold text-[14px] ${locked ? 'text-text-dim' : 'text-gold-bright'}">${escapeHtml(t('Choose one — permanent until respec'))}</p>
            <div class="flex items-start justify-center gap-3 sm:gap-8">
              ${nodes.map((n, i) => `
                ${i > 0 ? `<span class="self-center text-text-dim font-heading italic text-[14px] pt-6">${escapeHtml(t('or'))}</span>` : ''}
                ${nodeHtml(viewFor(n), true)}
              `).join('')}
            </div>
          </div>`
        : `<div class="grid grid-cols-4 gap-1.5 sm:gap-3 justify-items-center">${nodes.map((n) => nodeHtml(viewFor(n))).join('')}</div>`;
      const nextTier = (tier + 1) as Tier;
      const hasNext = tier < CAPSTONE_TIER && tree.some((n) => n.tier === nextTier);
      const connector = hasNext
        ? connectorHtml(spent >= TIER_UNLOCK_THRESHOLD[nextTier], nextTier === CAPSTONE_TIER)
        : '';
      return `<div class="flex flex-col">${tierHeaderHtml(tier, locked, spent)}${body}</div>${connector}`;
    }).join('');

    wireIconFallbacks(treeEl);
    treeEl.querySelectorAll<HTMLButtonElement>('[data-node]').forEach((btn) => {
      const id = btn.dataset.node!;
      btn.addEventListener('click', () => select(id));
    });
  }

  function renderDetail(): void {
    if (!character) return;
    const node = tree.find((n) => n.id === selectedId);
    if (!node) {
      detailEl.innerHTML = `<p class="text-[13px] text-text-dim">${escapeHtml(t('Choose a talent to see what it does.'))}</p>`;
      return;
    }
    const view = viewFor(node);
    const copy = talentCopy(node.id);
    const cls = character.characterClass as CharacterClassId;
    const current = effectLine(node.field, node.perRank, view.rank, cls);
    const next = view.maxed ? null : effectLine(node.field, node.perRank, view.rank + 1, cls);
    const unspent = character.talentPointsAvailable - character.talentPointsSpent;

    // Exactly one reason is shown, in the order the player would hit them.
    const blockedReason = view.locked
      ? tp(TIER_UNLOCK_THRESHOLD[node.tier as Tier],
        'Locked — spend {n} point in this tree first', 'Locked — spend {n} points in this tree first')
      : view.blockedByCapstone
        ? t('You already chose {name}. Respec to change it.', { name: talentCopy(takenCapstoneId() ?? '').name })
        : view.maxed ? t('Fully learned')
        : unspent <= 0 ? t('No unspent points — level up to earn more')
        : null;

    detailEl.innerHTML = `
      <div class="flex items-start gap-3">
        ${iconImgHtml(node, `w-16 h-16 rounded border border-border shrink-0 ${view.locked || view.blockedByCapstone ? 'talent-socket-locked' : ''}`)}
        <div class="min-w-0 flex flex-col gap-0.5">
          <h3 class="font-heading text-gold text-[16px] leading-tight">${escapeHtml(copy.name)}</h3>
          <p class="text-[11px] text-text-dim tabular-nums">
            ${escapeHtml(tierLabel(node.tier as Tier))} &middot; ${escapeHtml(t('rank {rank}/{max}', { rank: view.rank, max: node.maxRank }))}
          </p>
          ${node.isCapstone ? `<span class="chip chip-gold self-start mt-0.5 font-heading font-bold text-[12px]">${escapeHtml(t('Capstone'))}</span>` : ''}
        </div>
      </div>
      ${current || next ? `
        <div class="flex flex-col gap-1 border-t border-border/50 pt-2.5">
          ${current ? `<p class="text-[13px] text-text">${t('Now {effect}', { effect: `<span class="text-gold tabular-nums">${escapeHtml(current)}</span>` })}</p>` : ''}
          ${next ? `<p class="text-[13px] text-text-dim">${t('Next rank {effect}', { effect: `<span class="text-gold/80 tabular-nums">${escapeHtml(next)}</span>` })}</p>` : ''}
        </div>` : ''}
      ${copy.flavor ? `<p class="text-[13px] text-text-dim italic leading-snug">${escapeHtml(copy.flavor)}</p>` : ''}
      ${blockedReason ? `<p class="text-[12px] text-text-dim border-t border-border/50 pt-2.5">${escapeHtml(blockedReason)}</p>` : ''}
      <button id="tm-learn" type="button" ${view.learnable ? '' : 'disabled'}
        class="btn btn-gold mt-0.5 w-full">
        ${escapeHtml(view.maxed ? t('Learned') : view.rank > 0 ? t('Learn · rank {rank}', { rank: view.rank + 1 }) : t('Learn'))}
      </button>
    `;
    wireIconFallbacks(detailEl);
    const learnBtn = detailEl.querySelector<HTMLButtonElement>('#tm-learn');
    learnBtn?.addEventListener('click', () => void onAllocate(node.id));
  }

  function renderHeader(): void {
    if (!character) return;
    titleEl.textContent = t("{name}'s Talents", { name: character.name });
    subEl.textContent = t('{className} · Level {level}', { className: classDisplayName(character.characterClass), level: character.level });
    const unspent = character.talentPointsAvailable - character.talentPointsSpent;
    // "N unspent point(s)" — also the string the hub's tile badge mirrors.
    pointsEl.innerHTML = `
      <img src="${ICON_BASE}/_point.webp" alt="" aria-hidden="true" class="w-4 h-4 rounded-sm ${unspent > 0 ? '' : 'opacity-30 grayscale'}" />
      <span class="${unspent > 0 ? 'text-gold' : 'text-text-dim'}">${escapeHtml(tp(unspent, '{n} unspent point', '{n} unspent points'))}</span>
    `;
    respecBtn.disabled = busy || Object.keys(character.talents ?? {}).length === 0;
  }

  function render(): void {
    if (!character) return;
    // Default the selection so the detail panel is never empty on open —
    // prefer somewhere the player can actually spend.
    if (!selectedId || !tree.some((n) => n.id === selectedId)) {
      selectedId = (tree.find((n) => viewFor(n).learnable) ?? tree[0])?.id ?? null;
    }
    renderHeader();
    renderTree();
    renderDetail();
  }

  function select(nodeId: string): void {
    if (selectedId === nodeId) return;
    selectedId = nodeId;
    // Only the two things that depend on selection — re-rendering the whole
    // tree on every mouseenter would rebuild 7 nodes and drop focus.
    treeEl.querySelectorAll<HTMLElement>('[data-node]').forEach((btn) => {
      const socket = btn.firstElementChild as HTMLElement | null;
      const on = btn.dataset.node === nodeId;
      btn.setAttribute('aria-pressed', String(on));
      socket?.classList.toggle('talent-socket-active', on);
    });
    renderDetail();
  }

  async function reload(): Promise<void> {
    try {
      const rows = await listCharacters();
      character = rows.find((c) => c.id === characterId) ?? null;
      render();
    } catch (err) {
      showError(errorMessage(err));
    }
  }

  async function onAllocate(nodeId: string): Promise<void> {
    hideError();
    busy = true;
    render();
    try {
      await allocateTalent(characterId, nodeId);
      await reload();
      await onChanged();
    } catch (err) {
      showError(errorMessage(err));
    } finally {
      busy = false;
      render();
    }
  }

  async function onRespec(): Promise<void> {
    if (!confirm(t('Clear all talents for {cost} Wax?', { cost: CHARACTER.RESPEC_COST_WAX }))) return;
    hideError();
    busy = true;
    render();
    try {
      await respecCharacter(characterId);
      await reload();
      await onChanged();
    } catch (err) {
      showError(errorMessage(err));
    } finally {
      busy = false;
      render();
    }
  }

  closeBtn.addEventListener('click', close);
  respecBtn.addEventListener('click', () => void onRespec());
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  // First load needs the character row before the class-scoped tree fetch can run.
  void (async () => {
    try {
      const rows = await listCharacters();
      character = rows.find((c) => c.id === characterId) ?? null;
      if (!character) { showError(t('Character no longer exists.')); return; }
      const fetched = await getTalentTree(character.characterClass);
      // The roster carries four classes but talent-defs.ts defines three
      // trees (druid, added 2026-08-24 for role-based matchmaking, has none),
      // so this endpoint can legitimately answer with nothing. Degrade to an
      // honest empty state rather than throwing on `.filter` of undefined.
      tree = Array.isArray(fetched) ? fetched : [];
      if (tree.length === 0) {
        treeEl.innerHTML = `<p class="text-[13px] text-text-dim py-6 text-center">${escapeHtml(t('No talent tree has been written for this class yet.'))}</p>`;
        renderHeader();
        return;
      }
      render();
    } catch (err) {
      showError(errorMessage(err));
    }
  })();
}
